/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { collection, addDoc, doc, updateDoc, getDoc, query, where, getDocs, DocumentSnapshot, onSnapshot, QuerySnapshot, deleteDoc } from "firebase/firestore"
import { db } from "../../firebase"
import { toast } from 'react-toastify'
import dayjs from "dayjs"
import { tokens } from "../../theme"
import { useNavigate, useParams } from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"

const MONTHS = [
  { value: "04", label: "4月" },
  { value: "05", label: "5月" },
  { value: "06", label: "6月" },
  { value: "07", label: "7月" },
  { value: "08", label: "8月" },
  { value: "09", label: "9月" },
  { value: "10", label: "10月" },
  { value: "11", label: "11月" },
  { value: "12", label: "12月" },
  { value: "01", label: "1月" },
  { value: "02", label: "2月" },
  { value: "03", label: "3月" },
]

interface RecordRow {
  no: string
  implementationDate: string
  implementer: string
  inspector: string
  traps: Record<string, number>
}

interface PestControlData {
  id?: string
  year: string
  month: string
  manager: string
  verifier: string
  records: RecordRow[]
  trapNames: string[]
  remarks: string
  createdAt?: string
  updatedAt?: string
}

const PestControlManagementForm = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!(id && id !== 'new')

  const [processing, setProcessing] = useState<boolean>(false)
  const [year, setYear] = useState<string>(dayjs().format("YYYY"))
  const [month, setMonth] = useState<string>(dayjs().format("MM"))
  const [manager, setManager] = useState<string>("")
  const [verifier, setVerifier] = useState<string>("")
  const [records, setRecords] = useState<RecordRow[]>([])
  const [trapNames, setTrapNames] = useState<string[]>([])
  const [trapIds, setTrapIds] = useState<Record<string, string>>({}) // trapName -> docId mapping
  const [newTrapName, setNewTrapName] = useState<string>("")
  const [remarks, setRemarks] = useState<string>("")
  const [initialized, setInitialized] = useState<boolean>(false)

  // Load traps from Firestore
  useEffect(() => {
    const trapsRef = collection(db, "pestControlTraps")
    const unsubscribe = onSnapshot(trapsRef, (snapshot: QuerySnapshot) => {
      const names: string[] = []
      const ids: Record<string, string> = {}

      snapshot.docs.forEach(doc => {
        const trapName = doc.data().name
        names.push(trapName)
        ids[trapName] = doc.id
      })

      // Sort trap names
      names.sort()

      setTrapNames(names)
      setTrapIds(ids)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (initialized) return

    if (isEditMode && id) {
      loadRecord(id)
      setInitialized(true)
    } else if (!isEditMode && trapNames.length > 0 && records.length === 0) {
      // Check for existing record before initializing
      checkExistingRecord(year, month)
      setInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id, trapNames.length, initialized])

  const checkExistingRecord = async (targetYear: string, targetMonth: string) => {
    setProcessing(true)
    try {
      const q = query(
        collection(db, "pestControlManagements"),
        where("year", "==", targetYear),
        where("month", "==", targetMonth)
      )
      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        // Record exists, load it as edit mode
        const existingDoc = querySnapshot.docs[0]
        const record = {
          id: existingDoc.id,
          ...existingDoc.data()
        } as PestControlData

        toast.info("既に入力済みのデータがあります。編集モードで開きます。")

        setYear(record.year)
        setMonth(record.month)
        setManager(record.manager)
        setVerifier(record.verifier || "")
        setRecords(record.records || [])
        setRemarks(record.remarks || "")

        // Update URL to edit mode without page reload
        navigate(`/admin/pest-control-management/${existingDoc.id}`, { replace: true })
      } else {
        // No existing record, initialize with one empty record
        addNewRecord()
      }
    } catch (e) {
      console.error("Error checking existing record:", e)
      toast.error("データの確認に失敗しました")
      // Initialize with one empty record on error
      addNewRecord()
    } finally {
      setProcessing(false)
    }
  }

  const loadRecord = async (recordId: string) => {
    setProcessing(true)
    try {
      const docRef = doc(db, "pestControlManagements", recordId)
      const docSnap: DocumentSnapshot = await getDoc(docRef)

      if (docSnap.exists()) {
        const record = {
          id: docSnap.id,
          ...docSnap.data()
        } as PestControlData

        setYear(record.year)
        setMonth(record.month)
        setManager(record.manager)
        setVerifier(record.verifier || "")
        setRecords(record.records || [])
        // Don't set trapNames here - they come from the database
        setRemarks(record.remarks || "")
      } else {
        toast.error("レコードが見つかりません")
        navigate('/admin/pest-control-management')
      }
    } catch (e) {
      toast.error("データの読み込みに失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const addNewRecord = () => {
    const newRecord: RecordRow = {
      no: String(records.length + 1),
      implementationDate: "",
      implementer: "",
      inspector: "",
      traps: {}
    }

    // Initialize trap counts
    trapNames.forEach(trapName => {
      newRecord.traps[trapName] = 0
    })

    setRecords([...records, newRecord])
  }

  const removeRecord = (index: number) => {
    const newRecords = records.filter((_, i) => i !== index)
    // Renumber the records
    newRecords.forEach((record, i) => {
      record.no = String(i + 1)
    })
    setRecords(newRecords)
  }

  const updateRecord = (index: number, field: keyof RecordRow, value: any) => {
    const newRecords = [...records]
    if (field === 'traps') {
      newRecords[index].traps = value
    } else {
      (newRecords[index] as any)[field] = value
    }
    setRecords(newRecords)
  }

  const updateTrapCount = (recordIndex: number, trapName: string, count: number) => {
    const newRecords = [...records]
    newRecords[recordIndex].traps[trapName] = count
    setRecords(newRecords)
  }

  const addTrapName = async () => {
    if (newTrapName.trim() && !trapNames.includes(newTrapName.trim())) {
      const newName = newTrapName.trim()

      try {
        // Save to Firestore
        await addDoc(collection(db, "pestControlTraps"), {
          name: newName,
          createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
        })

        setNewTrapName("")
        toast.success(`トラップ「${newName}」を追加しました`)
      } catch (e) {
        toast.error("トラップの追加に失敗しました: " + JSON.stringify(e))
      }
    }
  }

  const removeTrapName = async (trapName: string) => {
    if (!window.confirm(`トラップ「${trapName}」を削除しますか？`)) {
      return
    }

    const trapId = trapIds[trapName]
    if (!trapId) {
      toast.error("トラップIDが見つかりません")
      return
    }

    try {
      // Delete from Firestore
      await deleteDoc(doc(db, "pestControlTraps", trapId))
      toast.success(`トラップ「${trapName}」を削除しました`)
    } catch (e) {
      toast.error("トラップの削除に失敗しました: " + JSON.stringify(e))
    }
  }

  const saveData = async () => {
    if (!manager) {
      toast.error("衛生管理者担当責任者を入力してください")
      return
    }

    setProcessing(true)
    const data = {
      year,
      month,
      manager,
      verifier,
      records,
      trapNames,
      remarks,
      updatedAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }

    try {
      if (isEditMode) {
        await updateDoc(doc(db, "pestControlManagements", id), data)
        toast.success("そ族昆虫防除記録を更新しました")
      } else {
        // Check if record already exists for this period
        const q = query(
          collection(db, "pestControlManagements"),
          where("year", "==", year),
          where("month", "==", month)
        )
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          toast.error("この月のデータは既に存在します")
          setProcessing(false)
          return
        }

        const newData = {
          ...data,
          createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
        }
        await addDoc(collection(db, "pestControlManagements"), newData)
        toast.success("そ族昆虫防除記録を保存しました")
      }
      navigate('/admin/pest-control-management')
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const handleBack = () => {
    navigate('/admin/pest-control-management')
  }

  return (
    <Box m={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {isEditMode ? 'そ族昆虫防除記録 編集' : 'そ族昆虫防除記録 新規作成'}
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        {/* 基本情報 */}
        <Typography variant="h6" fontWeight="bold" mb={2}>
          基本情報
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={3}>
          <TextField
            label="年度"
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            fullWidth
            disabled={isEditMode}
          />
          <FormControl fullWidth disabled={isEditMode}>
            <InputLabel>対象月</InputLabel>
            <Select
              value={month}
              label="対象月"
              onChange={(e) => setMonth(e.target.value)}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="衛生管理者担当責任者"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="確認者"
            value={verifier}
            onChange={(e) => setVerifier(e.target.value)}
            fullWidth
          />
        </Box>

        {/* トラップ名管理 */}
        <Typography variant="h6" fontWeight="bold" mt={4} mb={2}>
          トラップ名管理
        </Typography>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {trapNames.map((trapName, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: colors.primary[400],
                borderRadius: 1,
                px: 2,
                py: 1
              }}
            >
              <Typography>{trapName}</Typography>
              <IconButton
                size="small"
                onClick={() => removeTrapName(trapName)}
                sx={{ ml: 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Box display="flex" gap={2} mb={3}>
          <TextField
            label="新しいトラップ名"
            value={newTrapName}
            onChange={(e) => setNewTrapName(e.target.value)}
            size="small"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addTrapName()
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={addTrapName}
            disabled={!newTrapName.trim()}
          >
            追加
          </Button>
        </Box>

        {/* 実施記録 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={2}>
          <Typography variant="h6" fontWeight="bold">
            実施記録
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addNewRecord}
          >
            記録を追加
          </Button>
        </Box>

        <Typography variant="body2" color="textSecondary" mb={2}>
          月に１回実施すること。捕獲された数を記入。異常時の対応は特記事項に記入。
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                <TableCell sx={{ fontWeight: "bold", minWidth: 60 }}>No</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>実施日</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>実施者</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>確認者</TableCell>
                {trapNames.map((trapName, index) => (
                  <TableCell key={index} sx={{ fontWeight: "bold", minWidth: 100 }}>
                    {trapName}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: "bold", minWidth: 80 }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record, recordIndex) => (
                <TableRow key={recordIndex}>
                  <TableCell>{record.no}</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      value={record.implementationDate}
                      onChange={(e) => updateRecord(recordIndex, 'implementationDate', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={record.implementer}
                      onChange={(e) => updateRecord(recordIndex, 'implementer', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={record.inspector}
                      onChange={(e) => updateRecord(recordIndex, 'inspector', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  {trapNames.map((trapName, trapIndex) => (
                    <TableCell key={trapIndex}>
                      <TextField
                        type="number"
                        value={record.traps[trapName] || 0}
                        onChange={(e) => updateTrapCount(recordIndex, trapName, parseInt(e.target.value) || 0)}
                        size="small"
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeRecord(recordIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4 + trapNames.length + 1} align="center">
                    記録がありません。「記録を追加」ボタンをクリックしてください。
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 特記事項 */}
        <Typography variant="h6" fontWeight="bold" mt={3} mb={2}>
          特記事項
        </Typography>
        <TextField
          multiline
          rows={4}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          placeholder="異常時の対応等を記入してください"
        />

        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={handleBack}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={saveData}
            disabled={processing}
          >
            {isEditMode ? "更新" : "保存"}
          </Button>
        </Box>
      </Paper>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={processing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

export default PestControlManagementForm
